import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Search, Database, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR } from "@/lib/format";

const CATEGORIAS = [
  { v: "produccion", l: "Producción" },
  { v: "montaje",    l: "Montaje" },
  { v: "diseno",     l: "Diseño" },
  { v: "externo",    l: "Externo" },
  { v: "otros",      l: "Otros" },
];

const UNIDADES = ["ud", "m2", "ml", "hora", "km", "dia", "lote"];

const empty = { nombre: "", categoria: "produccion", unidad: "m2", precio: 0, coste: 0, descripcion: "", activo: true, orden: 0 };


export default function CatalogoPrecios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filtroCat, setFiltroCat] = useState("todas");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/catalogo-precios");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    return items.filter((c) => {
      if (filtroCat !== "todas" && c.categoria !== filtroCat) return false;
      if (q) {
        const t = q.toLowerCase();
        if (!(c.nombre || "").toLowerCase().includes(t) && !(c.descripcion || "").toLowerCase().includes(t)) return false;
      }
      return true;
    });
  }, [items, q, filtroCat]);

  const guardar = async () => {
    try {
      if (editing) {
        await api.patch(`/catalogo-precios/${editing.id}`, form);
        toast.success("Concepto actualizado");
      } else {
        await api.post("/catalogo-precios", form);
        toast.success("Concepto creado");
      }
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) {
      toast.error("Error guardando: " + (e?.response?.data?.detail || e.message));
    }
  };

  const editar = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c });
    setOpen(true);
  };

  const borrar = async (c) => {
    if (!window.confirm(`¿Borrar "${c.nombre}"?`)) return;
    await api.delete(`/catalogo-precios/${c.id}`);
    toast.success("Concepto borrado");
    load();
  };

  const seedLogomedia = async () => {
    if (!window.confirm("Cargará 102 conceptos de la tarifa Logomedia 2024. Los que ya existan se omiten. ¿Continuar?")) return;
    try {
      const { data } = await api.post("/catalogo-precios/seed-logomedia");
      toast.success(`Cargados ${data.creados} nuevos · ${data.ya_existian} ya existían`);
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-6" data-testid="catalogo-precios">
      <PageHeader
        title="Catálogo de precios DNF"
        subtitle="Conceptos reutilizables para construir presupuestos rápidamente."
        action={
          <div className="flex gap-2">
            <Button onClick={seedLogomedia} variant="outline"
              data-testid="btn-seed-logomedia"
              className="border-zinc-700 hover:bg-zinc-800">
              <Database className="w-4 h-4 mr-2" /> Cargar tarifa Logomedia
            </Button>
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
              data-testid="btn-nuevo-concepto"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Nuevo concepto
            </Button>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="input-buscar-catalogo"
            placeholder="Buscar concepto…"
            className="pl-9 bg-zinc-900/60 border-zinc-800" />
        </div>
        <Select value={filtroCat} onValueChange={setFiltroCat}>
          <SelectTrigger className="w-44 bg-zinc-900/60 border-zinc-800" data-testid="select-filtro-cat">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60">
            <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400">
              <th className="px-4 py-3">Concepto</th>
              <th className="px-4 py-3">Categoría</th>
              <th className="px-4 py-3">Unidad</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3 text-right">Coste</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Cargando…</td></tr>}
            {!loading && filtrados.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">
                {items.length === 0 ? "Catálogo vacío. Pulsa \"Cargar tarifa Logomedia\" para empezar." : "Sin resultados."}
              </td></tr>
            )}
            {filtrados.map((c) => {
              const catLabel = CATEGORIAS.find((x) => x.v === c.categoria)?.l || c.categoria;
              return (
                <tr key={c.id} data-testid={`cat-row-${c.id}`}
                    className="border-t border-zinc-800/70 hover:bg-zinc-900/40">
                  <td className="px-4 py-3 text-zinc-100">
                    <div>{c.nombre}</div>
                    {c.descripcion && <div className="text-[11px] text-zinc-500 mt-0.5">{c.descripcion}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{catLabel}</td>
                  <td className="px-4 py-3 text-zinc-300">{c.unidad}</td>
                  <td className="px-4 py-3 text-right text-amber-300 font-semibold">{fmtEUR(c.precio)}</td>
                  <td className="px-4 py-3 text-right text-zinc-400">{c.coste ? fmtEUR(c.coste) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => editar(c)}
                      data-testid={`btn-edit-${c.id}`}
                      className="h-7 px-2 text-zinc-400 hover:text-amber-300">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => borrar(c)}
                      data-testid={`btn-del-${c.id}`}
                      className="h-7 px-2 text-zinc-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="dialog-concepto">
          <DialogHeader>
            <DialogTitle className="text-amber-300">{editing ? "Editar concepto" : "Nuevo concepto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-zinc-400">Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                data-testid="input-nombre" className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Categoría</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {CATEGORIAS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Unidad</Label>
                <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-unidad"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {UNIDADES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Precio venta (€/{form.unidad})</Label>
                <Input type="number" step="0.01" value={form.precio}
                  onChange={(e) => setForm({ ...form, precio: parseFloat(e.target.value) || 0 })}
                  data-testid="input-precio" className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Coste (opcional)</Label>
                <Input type="number" step="0.01" value={form.coste || 0}
                  onChange={(e) => setForm({ ...form, coste: parseFloat(e.target.value) || 0 })}
                  data-testid="input-coste" className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Descripción / notas</Label>
              <Textarea value={form.descripcion || ""}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                data-testid="input-descripcion"
                className="bg-zinc-900 border-zinc-800 min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="btn-cancel-concepto">Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-save-concepto"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              {editing ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
